import React, { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";
import * as faceapi from "face-api.js";
import styles from "./FaceRegistration.module.css";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import DataUserLoyalty from "../store/DataUserLoyalty";
import { fetchData } from "../service/Service";

const FaceRegistration = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuth = location.pathname.startsWith("/userLoyalty/auth");
  const [isRegistering, setIsRegistering] = useState(false);
  const [step, setStep] = useState(1);
  const [images, setImages] = useState({
    front: null,
    left: null,
    right: null,
  });
  const [message, setMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [isAutoCapture, setIsAutoCapture] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const webcamRef = useRef(null);
  const modelsLoaded = useRef(false);
  const detectionInterval = useRef(null);
  const [notRegister, setNotRegister] = useState(false);
  const [isPending, setIsPending] = useState(false);
  useEffect(() => {
    const loadModels = async () => {
      try {
        setMessage("Загрузка моделей распознавания...");
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri("/models"),
          faceapi.nets.faceLandmark68Net.loadFromUri("/models"),
        ]);
        modelsLoaded.current = true;
        setMessage("Модели загружены успешно");
        setTimeout(() => setMessage(""), 2000);
      } catch (error) {
        setMessage("Ошибка загрузки моделей. Обновите страницу.");
        console.error("Ошибка загрузки моделей:", error);
      }
    };

    loadModels();

    return () => {
      if (detectionInterval.current) clearInterval(detectionInterval.current);
    };
  }, []);

  const toggleAutoCapture = () => {
    setIsAutoCapture(!isAutoCapture);
    if (!isAutoCapture && isRegistering) {
      startFaceDetection();
    } else if (detectionInterval.current) {
      clearInterval(detectionInterval.current);
    }
  };

  const startRegistration = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      stream.getTracks().forEach((track) => track.stop());

      setIsRegistering(true);
      setIsComplete(false);
      setStep(1);
      setProgress(0);
      setImages({ front: null, left: null, right: null });
      setMessage("Расположите лицо внутри овала");
      setShowPreview(false);
      startFaceDetection();
    } catch (err) {
      setMessage(`Ошибка камеры: ${err.message}`);
    }
  };

  const startFaceDetection = () => {
    if (detectionInterval.current) clearInterval(detectionInterval.current);

    detectionInterval.current = setInterval(async () => {
      if (!webcamRef.current || !modelsLoaded.current || showPreview) return;

      const imageSrc = webcamRef.current.getScreenshot();
      const image = new Image();
      image.src = imageSrc;

      try {
        const detections = await faceapi
          .detectAllFaces(image, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks();

        if (detections.length === 0) {
          setMessage("Лицо не обнаружено. Расположите лицо в овале.");
          setProgress(0);
          return;
        }

        const landmarks = detections[0].landmarks;
        const jawOutline = landmarks.getJawOutline();
        const faceWidth = jawOutline[jawOutline.length - 1].x - jawOutline[0].x;
        const qualityScore = Math.min(
          100,
          Math.max(25, Math.floor((faceWidth * 100) / 150))
        );
        setProgress(qualityScore);

        // Calculate face direction
        const leftEye = landmarks.getLeftEye();
        const rightEye = landmarks.getRightEye();
        const nose = landmarks.getNose();

        const leftEyeCenter =
          leftEye.reduce((sum, point) => sum + point.x, 0) / leftEye.length;
        const rightEyeCenter =
          rightEye.reduce((sum, point) => sum + point.x, 0) / rightEye.length;
        const noseTipX = nose[6].x;

        const faceDirection =
          rightEyeCenter - leftEyeCenter > 0
            ? (noseTipX - leftEyeCenter) / (rightEyeCenter - leftEyeCenter)
            : 0;

        let orientationValid = false;
        let orientationMessage = "";

        if (step === 1) {
          // Front
          orientationValid =
            Math.abs(faceDirection - 0.5) < 0.2 && qualityScore > 40;
          orientationMessage = "Расположите лицо прямо в овале";
        } else if (step === 2) {
          // Left turn
          orientationValid = faceDirection < 0.3 && qualityScore > 25;
          orientationMessage = "Поверните голову влево";
        } else if (step === 3) {
          // Right turn
          orientationValid = faceDirection > 0.7 && qualityScore > 25;
          orientationMessage = "Поверните голову вправо";
        }

        if (!orientationValid) {
          setMessage(orientationMessage);
          return;
        }

        // AUTO-CAPTURE LOGIC
        if (isAutoCapture && orientationValid && !showPreview) {
          handleCapture(imageSrc);
        } else if (orientationValid) {
          setMessage(`${orientationMessage} (Готово к сохранению)`);
        }
      } catch (error) {
        console.error("Ошибка распознавания:", error);
      }
    }, 500);
  };

  // Update the useEffect for step changes
  useEffect(() => {
    if (isRegistering && !showPreview) {
      startFaceDetection();
    }
  }, [step, isRegistering, showPreview]);

  const handleCapture = (imageSrc) => {
    setShowPreview(true);

    if (step === 1) {
      setImages((prev) => ({ ...prev, front: imageSrc }));
      setMessage("Фронтальное фото сохранено");
    } else if (step === 2) {
      setImages((prev) => ({ ...prev, left: imageSrc }));
      setMessage("Поворот влево сохранен");
    } else if (step === 3) {
      setImages((prev) => ({ ...prev, right: imageSrc }));
      setMessage("Поворот вправо сохранен");
    }

    // Clear detection interval while showing preview
    if (detectionInterval.current) {
      clearInterval(detectionInterval.current);
    }
  };

  const confirmPhoto = () => {
    setShowPreview(false);

    if (step === 1) {
      completeRegistration();
      return;
    }
    // else if (step === 2) {
    //     setStep(3);
    //     setMessage("Поверните голову вправо");
    // } else if (step === 3) {
    //     completeRegistration();
    //     return;
    // }

    setProgress(0);
    startFaceDetection();
  };

  const retakePhoto = () => {
    setShowPreview(false);
    setProgress(0);

    if (step === 1) {
      setImages((prev) => ({ ...prev, front: null }));
    } else if (step === 2) {
      setImages((prev) => ({ ...prev, left: null }));
    } else if (step === 3) {
      setImages((prev) => ({ ...prev, right: null }));
    }

    setMessage(
      step === 1
        ? "Расположите лицо внутри овала"
        : step === 2
          ? "Поверните голову влево"
          : "Поверните голову вправо"
    );

    startFaceDetection();
  };

  const manualCapture = () => {
    if (!webcamRef.current || showPreview) return;
    const imageSrc = webcamRef.current.getScreenshot();
    handleCapture(imageSrc);
  };

  const completeRegistration = async () => {
    clearInterval(detectionInterval.current);
    console.log("Фронтальное фото:", images.front);
    console.log("Левая сторона:", images.left);
    console.log("Правая сторона:", images.right);

    if (location.pathname.startsWith("/userLoyalty/registration")) {
      setMessage("Регистрация лица завершена успешно!");
      setIsComplete(true);
      setIsRegistering(false);
    }
    DataUserLoyalty.images.push(images.front, images.left, images.right);
    if (isAuth) {
      console.log(DataUserLoyalty.placeId);
      const userData = {
        place_id: DataUserLoyalty.placeId
          ? DataUserLoyalty.placeId
          : "blna25d8e8",
        // place_id: "blna25d8e8",
        data_url: DataUserLoyalty.images,
      };
      setMessage("Регистрация лица завершена успешно!");
      setIsPending(true);
      setIsComplete(true);
      setIsRegistering(false);
      const response = await fetchData(JSON.stringify(userData));
      console.log(response);
      DataUserLoyalty.images = [];
      DataUserLoyalty.userDate = response;

      if (response.statusCode === 200) {
        // message.success("Вход успешен");
        setMessage("Регистрация лица завершена успешно!");
        setIsPending(false);

        navigate(
          `/userLoyalty/main${
            DataUserLoyalty.placeId
              ? `?place_id=${DataUserLoyalty.placeId}`
              : ""
          }`
        );
      }
      if (response.statusCode === 201 || response.massage == "User Not Found") {
        setIsPending(false);
        setNotRegister(true);
        setIsRegistering(false);
        setIsComplete(true);
        setTimeout(() => {
          navigate(
            `/userLoyalty/registration${
              DataUserLoyalty.placeId
                ? `?place_id=${DataUserLoyalty.placeId}`
                : ""
            }`
          );
        }, 3000);
      }

      setTimeout(() => {
        setStep(1);
        setImages({ front: null, left: null, right: null });
        setIsComplete(false);
        setShowPreview(false);
      }, 3000);
    }
  };

  const cancelRegistration = () => {
    setIsRegistering(false);
    setStep(1);
    setMessage("");
    setProgress(0);
    setShowPreview(false);
    clearInterval(detectionInterval.current);
  };

  const getStepInstruction = () => {
    switch (step) {
      case 1:
        return "Фронтальное фото";
      case 2:
        return "Поворот влево";
      case 3:
        return "Поворот вправо";
      default:
        return "";
    }
  };

  const getCurrentImage = () => {
    switch (step) {
      case 1:
        return images.front;
      case 2:
        return images.left;
      case 3:
        return images.right;
      default:
        return null;
    }
  };

  const massage = isAuth ? "Вход в систему" : "Лицо успешно зарегистрировано";

  return (
    <div className={styles.container}>
      {!isRegistering && !isComplete ? (
        <div className={styles.startScreen}>
          <h1 className={styles.title}>
            {isAuth ? "Сканирование лица" : "Регистрация лица"}
          </h1>
          <p className={styles.subtitle}>
            {isAuth
              ? "Следуйте инструкциям на экране для входа в систему"
              : "Следуйте инструкциям на экране для завершения регистрации"}
          </p>
          <button onClick={startRegistration} className={styles.startButton}>
            {isAuth ? "Войти в систему" : "Начать регистрацию"}
          </button>
          <div>
            {isAuth && (
              <p>
                У вас нет аккаунта?{" "}
                <NavLink
                  className={styles.join}
                  to={`/userLoyalty/registration${
                    DataUserLoyalty.placeId
                      ? `?place_id=${DataUserLoyalty.placeId}`
                      : ""
                  }`}
                >
                  Зарегистрироваться
                </NavLink>
              </p>
            )}
          </div>
        </div>
      ) : isPending ? (
        <div className={styles.completeScreen}>
          <h1 className={styles.title}>Загрузка</h1>
        </div>
      ) : isComplete ? (
        <div className={styles.completeScreen}>
          {notRegister ? "" : <div className={styles.checkmark}>✓</div>}

          <h1 className={styles.title}>
            {notRegister ? "Пользователь не найден" : "Успешно!"}
          </h1>
          <p className={styles.successMessage}>
            {notRegister ? "пройдите регистрацию" : massage}
          </p>
        </div>
      ) : (
        <div className={styles.registrationContainer}>
          <div className={styles.cameraContainer}>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat='image/png'
              videoConstraints={{
                facingMode: "user",
                width: { ideal: 1280 },
                height: { ideal: 720 },
              }}
              mirrored={true}
              className={styles.camera}
            />
            <div className={styles.ovalOverlay}></div>
            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>{progress}%</span>
            </div>
          </div>

          <div className={styles.controlPanel}>
            <div className={styles.stepIndicator}>
              {[1].map((num) => (
                <React.Fragment key={num}>
                  <div
                    className={`${styles.step} ${
                      step === num ? styles.active : ""
                    } ${step > num ? styles.completed : ""}`}
                  >
                    {step > num ? "✓" : num}
                  </div>
                  {num < 1 && <div className={styles.stepLine}></div>}
                </React.Fragment>
              ))}
            </div>

            <h3 className={styles.stepTitle}>{getStepInstruction()}</h3>
            <p className={styles.message}>{message}</p>

            {(showPreview || getCurrentImage()) && (
              <div className={styles.previewContainer}>
                <div className={styles.imagePreview}>
                  <img src={getCurrentImage()} alt='Предпросмотр' />
                  <div className={styles.buttonGroup}>
                    <button
                      onClick={retakePhoto}
                      className={styles.retakeButton}
                    >
                      Переснять
                    </button>
                    <button
                      onClick={confirmPhoto}
                      className={styles.captureButton}
                      style={{ flex: 2 }}
                    >
                      {step === 1 ? "Завершить" : "Продолжить"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!showPreview && (
              <>
                <div className={styles.buttonGroup}>
                  <button
                    onClick={toggleAutoCapture}
                    className={`${styles.toggleButton} ${
                      isAutoCapture ? styles.active : ""
                    }`}
                  >
                    {isAutoCapture ? "Авто-захват: ВКЛ" : "Авто-захват: ВЫКЛ"}
                  </button>
                  <button
                    onClick={manualCapture}
                    className={styles.captureButton}
                  >
                    Сделать снимок
                  </button>
                </div>

                <button
                  onClick={cancelRegistration}
                  className={styles.cancelButton}
                >
                  {isAuth ? "Отменить вход" : "Отменить регистрацию"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default FaceRegistration;
